// Vendor Libraries
import { createSelector } from '@ngrx/store';
import memoize from 'lodash.memoize';
// Utils / Constants
import { BETWEEN, EQUAL, FUND_SCREENER_INFO_TYPE, FUND_SCREENER_METRIC_TYPE, GREATER_THAN, GREATER_THAN_EQUAL, LESS_THAN, LESS_THAN_EQUAL, METRIC_FILTER_MODAL_TABS, METRIC_FILTER_PARTIAL_SIDE_LEFT, METRIC_FILTER_PARTIAL_SIDE_RIGHT, PERCENTILE_SEQUENTIAL, PERCENTILE_UNIVERSE, RANK_SEQUENTIAL, RANK_UNIVERSE, STOCK_SCREENER_IDENTIFIER, STOCK_SCREENER_INFO_TYPE, STOCK_SCREENER_METRIC_TYPE, } from 'ScreenerApp/ts/features/screener/constants';
import { INVALID_REASON, VALIDATION_STATUS, allowNextTokenToBeOperator, validateMetricFilterExpression, validateMetricFilterPartial, validatePercentilePartialState, validateRangePartialState, validateRankPartialState, } from 'ScreenerApp/ts/features/screener/state/metric-filter-builder/metric-filter-builder.validators';
import { getBasicFormatted } from 'NewAngular/utils/number_formatter';
// Selectors
import { getMetricFilterPartialDescriptionForToken } from 'ScreenerApp/ts/features/screener/state/metric-filters/metric-filters-utils';
import { getScreenerState } from 'ScreenerApp/ts/features/screener/state/screener-base.selectors';
export const getMetricFilterBuilder = createSelector(getScreenerState, (state) => state.metricFilterBuilder);
export function getLabelMapTypes(screenerType) {
    if (screenerType === STOCK_SCREENER_IDENTIFIER) {
        return [STOCK_SCREENER_METRIC_TYPE, STOCK_SCREENER_INFO_TYPE];
    }
    return [FUND_SCREENER_METRIC_TYPE, FUND_SCREENER_INFO_TYPE];
}
export const getMetricFilterBuilderState = createSelector(getScreenerState, (state) => state.metricFilterBuilder);
const getLeftMetricFilterPartialState = (state) => getMetricFilterBuilderState(state).leftMetricFilterTokens;
const getRightMetricFilterPartialState = (state) => getMetricFilterBuilderState(state).rightMetricFilterTokens;
export const getMetricFilterPartialState = (state, metricFilterPartial) => {
    const metricFilterBuilderState = getMetricFilterBuilderState(state);
    switch (metricFilterPartial) {
        case METRIC_FILTER_PARTIAL_SIDE_LEFT:
            return metricFilterBuilderState.leftMetricFilterTokens;
        case METRIC_FILTER_PARTIAL_SIDE_RIGHT:
            return metricFilterBuilderState.rightMetricFilterTokens;
        default:
            return [];
    }
};
const formatMetricFilterEquationItems = (metricFilterPartialState, metricLabelMap) => {
    if (!metricLabelMap)
        return [];
    return metricFilterPartialState.map((token) => ({
        id: token,
        displayLabel: getMetricFilterPartialDescriptionForToken(token, metricLabelMap),
    }));
};
export const getMetricFilterEquationItems = (state, metricLabelMap, metricFilterPartial) => {
    if ([METRIC_FILTER_PARTIAL_SIDE_LEFT, METRIC_FILTER_PARTIAL_SIDE_RIGHT].includes(metricFilterPartial)) {
        const metricFilterPartialState = getMetricFilterPartialState(state, metricFilterPartial);
        return formatMetricFilterEquationItems(metricFilterPartialState, metricLabelMap);
    }
    return [];
};
export const getMetricFilterModalActiveTab = createSelector(getMetricFilterBuilderState, (metricFilterBuilderState) => {
    const middleMetricToken = metricFilterBuilderState.middleMetricFilterToken;
    switch (middleMetricToken) {
        case PERCENTILE_SEQUENTIAL:
        case PERCENTILE_UNIVERSE:
            return METRIC_FILTER_MODAL_TABS.PERCENTILE;
        case RANK_SEQUENTIAL:
        case RANK_UNIVERSE:
            return METRIC_FILTER_MODAL_TABS.RANK;
        case GREATER_THAN:
        case GREATER_THAN_EQUAL:
        case LESS_THAN:
        case LESS_THAN_EQUAL:
        case EQUAL:
            return METRIC_FILTER_MODAL_TABS.EQUATION;
        case BETWEEN:
        default:
            return METRIC_FILTER_MODAL_TABS.RANGE;
    }
});
export const getEditingTokenOriginalValue = createSelector(getScreenerState, (screenerState) => {
    const metricFilterBuilderState = screenerState.metricFilterBuilder;
    return metricFilterBuilderState.editingTokenValue;
});
export const getPopoverStatusForToken = (state, metricFilterTokenIndex, metricFilterPartialSide) => {
    const screenerState = getScreenerState(state);
    const { visiblePopoverToken } = screenerState.metricFilterBuilder;
    return visiblePopoverToken.index === metricFilterTokenIndex && visiblePopoverToken.side === metricFilterPartialSide;
};
export const getSelectedRangeData = createSelector(getScreenerState, (screenerState) => {
    const metricFilterBuilderState = screenerState.metricFilterBuilder;
    const selectedMetric = metricFilterBuilderState.leftMetricFilterTokens[0];
    return screenerState.rangeData.data[selectedMetric];
});
export const getSelectedRangeValues = createSelector(getScreenerState, (screenerState) => {
    const metricFilterBuilderState = screenerState.metricFilterBuilder;
    // if the `range` tab is not active, short circuit.
    if (metricFilterBuilderState.middleMetricFilterToken !== BETWEEN)
        return [null, null];
    const selectedMetric = metricFilterBuilderState.leftMetricFilterTokens[0];
    const metricRangeData = screenerState.rangeData.data[selectedMetric];
    // if we don't have a selected metric or have not loaded the `rangeData`, short circuit.
    if (selectedMetric === undefined || metricRangeData === undefined)
        return [null, null];
    const [selectedLowerBound, selectedUpperBound] = metricFilterBuilderState.rightMetricFilterTokens;
    return [selectedLowerBound, selectedUpperBound];
});
export const getFormattedSelectedRangeValues = createSelector(getSelectedRangeData, getSelectedRangeValues, (metricRangeData, selectedRangeValues) => {
    if (metricRangeData === undefined)
        return [null, null];
    const metricType = metricRangeData.type;
    const [selectedLowerBound, selectedUpperBound] = selectedRangeValues;
    const formattedLowerBound = selectedLowerBound !== undefined && selectedLowerBound !== null
        ? getBasicFormatted(selectedLowerBound, metricType)
        : null;
    const formattedUpperBound = selectedUpperBound !== undefined && selectedUpperBound !== null
        ? getBasicFormatted(selectedUpperBound, metricType)
        : null;
    return [formattedLowerBound, formattedUpperBound];
});
/*
 * Validation selectors
 * */
const createLeftMetricFilterEquationValidationStatusSelector = (metricLabelMap) => createSelector(getLeftMetricFilterPartialState, (leftMetricFilterPartialState) => {
    return validateMetricFilterPartial(leftMetricFilterPartialState, metricLabelMap);
});
const createRightMetricFilterEquationValidationStatusSelector = (metricLabelMap) => createSelector(getRightMetricFilterPartialState, (rightMetricFilterPartialState) => {
    return validateMetricFilterPartial(rightMetricFilterPartialState, metricLabelMap);
});
export const getMetricFilterEquationValidationStatus = (state, metricLabelMap, metricFilterPartial) => {
    switch (metricFilterPartial) {
        case METRIC_FILTER_PARTIAL_SIDE_LEFT:
            return createLeftMetricFilterEquationValidationStatusSelector(metricLabelMap)(state);
        case METRIC_FILTER_PARTIAL_SIDE_RIGHT:
            return createRightMetricFilterEquationValidationStatusSelector(metricLabelMap)(state);
        default:
            return { index: null, status: VALIDATION_STATUS.INCOMPLETE, reason: null };
    }
};
export const getPercentileStateValidationStatus = createSelector(getRightMetricFilterPartialState, (rightMetricFilterPartial) => {
    return validatePercentilePartialState(rightMetricFilterPartial);
});
export const getRankStateValidationStatus = createSelector(getRightMetricFilterPartialState, (rightMetricFilterPartial) => {
    return validateRankPartialState(rightMetricFilterPartial);
});
export const getRangeStateValidationStatus = createSelector(getRightMetricFilterPartialState, (rightMetricFilterPartial) => {
    return validateRangePartialState(rightMetricFilterPartial);
});
export const createGlobalStateValidationStatusSelector = (metricLabelMap) => memoize(createSelector(getLeftMetricFilterPartialState, getRightMetricFilterPartialState, (leftMetricFilterTokens, rightMetricFilterTokens) => {
    return validateMetricFilterExpression(leftMetricFilterTokens, rightMetricFilterTokens, metricLabelMap);
}));
const ERROR_MESSAGE_MAP = {
    [INVALID_REASON.SEPARATE_OPERATORS]: 'Operators must be separated by a financial metric or a number.',
    [INVALID_REASON.SEPARATE_METRICS]: 'Financial metrics and numbers must be separated with an operator.',
    [INVALID_REASON.SEPARATE_PARENTHESES]: 'Parentheses must be separated by an operator.',
    [INVALID_REASON.PARENTHESIS_BALANCE]: 'The number of open parentheses does not match the number of close parentheses.', // eslint-disable-line max-len
    [INVALID_REASON.SEPARATE_OPEN_PARENTHESIS]: 'An open parenthesis must be followed by a financial metric, number or open parenthesis.', // eslint-disable-line max-len
    [INVALID_REASON.SEPARATE_CLOSING_PARENTHESIS]: 'A close parenthesis should be followed by an operator or another close parenthesis.', // eslint-disable-line max-len
    [INVALID_REASON.BAD_INPUT]: 'Please enter a single operator, number or financial metric at a time.',
    [INVALID_REASON.STARTING_CLOSING_PARENTHESIS]: 'An expression cannot start with a close parenthesis.',
    [INVALID_REASON.PERCENTILE_CHOICES_INVALID]: 'Quantile options are invalid.',
    [INVALID_REASON.RANK_CHOICES_INVALID]: 'Rank options are invalid.',
    [INVALID_REASON.RANK_ORDINAL_INVALID]: 'Rank must be an integer greater than 0.',
    [INVALID_REASON.BETWEEN_RANGE]: 'Please select a valid min and max.',
    [INVALID_REASON.MINIMUM_METRIC]: 'A financial filter must contain at least 1 financial metric.',
};
export const getValidationErrorMessage = (validationObject) => {
    return (validationObject.reason && ERROR_MESSAGE_MAP[validationObject.reason]) || '';
};
export const getIsInvalidStatusForToken = (validationObject, metricFilterTokenIndex) => {
    // If the expression is invalid, check if this particular `metricFilterTokenIndex`
    // is at or precedes the invalid index. That means it is part of the error in the expression.
    return (validationObject.status === VALIDATION_STATUS.INVALID &&
        (validationObject.index === metricFilterTokenIndex ||
            (validationObject.index !== null && validationObject.index - 1 === metricFilterTokenIndex)));
};
export const nextTokenCanBeOperator = (state, metricLabelMap, metricFilterPartialSide, itemIndex = undefined) => {
    // Based on the preceding token, determine if we can allow an operator at this point in the expression.
    // This is useful when distinguishing between a minus sign as an operator or a leading negative sign.
    const metricFilterPartial = getMetricFilterPartialState(state, metricFilterPartialSide);
    const precedingIndex = itemIndex !== undefined ? itemIndex - 1 : metricFilterPartial.length - 1;
    const precedingToken = metricFilterPartial[precedingIndex];
    return allowNextTokenToBeOperator(precedingToken, metricLabelMap);
};
export const createGlobalMetricFilterModalValidationStatusSelector = (metricLabelMap) => memoize(createSelector(createLeftMetricFilterEquationValidationStatusSelector(metricLabelMap), getMetricFilterModalActiveTab, getRangeStateValidationStatus, createRightMetricFilterEquationValidationStatusSelector(metricLabelMap), getPercentileStateValidationStatus, getRankStateValidationStatus, createGlobalStateValidationStatusSelector(metricLabelMap), (leftMetricFilterEquationValidationStatus, metricFilterModalActiveTab, rangeStateValidationStatus, rightMetricFilterEquationValidationStatus, percentileStateValidationStatus, rankStateValidationStatus, globalStateValidationStatus) => {
    let rightPartialValidationStatus;
    switch (metricFilterModalActiveTab) {
        case METRIC_FILTER_MODAL_TABS.RANGE:
            rightPartialValidationStatus = rangeStateValidationStatus;
            break;
        case METRIC_FILTER_MODAL_TABS.EQUATION:
            rightPartialValidationStatus = rightMetricFilterEquationValidationStatus;
            break;
        case METRIC_FILTER_MODAL_TABS.PERCENTILE:
            rightPartialValidationStatus = percentileStateValidationStatus;
            break;
        default:
            rightPartialValidationStatus = rankStateValidationStatus;
            break;
    }
    // if all three validators return `VALID`, the metric filter is considered valid.
    return (leftMetricFilterEquationValidationStatus.status === VALIDATION_STATUS.VALID &&
        rightPartialValidationStatus.status === VALIDATION_STATUS.VALID &&
        globalStateValidationStatus.status === VALIDATION_STATUS.VALID);
}));
